import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useNav } from '../../store/nav';
import { useOnboarding } from '../../store/onboarding';
import { useToast } from '../../store/toast';
import { useMe } from '../../hooks/useMe';
import { useBackHandler } from '../../hooks/useBackHandler';
import { resetWalletApi } from '../../lib/me';
import { getWalletSeedOrThrow, clearAllSeeds } from '../../lib/wallet';
import { useWallets, useActiveWallet } from '../../store/wallets';
import { haptic } from '../../hooks/useTelegram';
import { mockSeed } from '../../lib/mock';
import { FigIcon } from '../FigIcon';
import { ReauthGate } from './ReauthGate';

type Phase = 'reveal' | 'revealed' | 'warn' | 'reset';

/** View-seed screen for one wallet (`walletId`, else the active wallet).
 *  Re-authenticates (PIN/biometric) via ReauthGate, shows that wallet's own
 *  mnemonic, and offers a destructive "Reset your wallet" flow: warning +
 *  acknowledgement → a second reauth → wipes every wallet (server + device) and
 *  returns the user to onboarding. */
export function SeedScreen({ walletId }: { walletId?: string }) {
  const { t } = useTranslation();
  const setTab = useNav((s) => s.setTab);
  const onboardingReset = useOnboarding((s) => s.reset);
  const showToast = useToast((s) => s.show);
  const { token } = useMe();
  const qc = useQueryClient();
  const wallets = useWallets((s) => s.wallets);
  const active = useActiveWallet();
  const wallet = wallets.find((w) => w.id === walletId) ?? active;
  // Legacy wallets derived from the old shared seed all reveal the same phrase.
  const sharedWith =
    wallet && (wallet.seedKind ?? 'account') === 'account'
      ? wallets.filter((w) => w.id !== wallet.id && (w.seedKind ?? 'account') === 'account')
      : [];

  const [phase, setPhase] = useState<Phase>('reveal');
  const [words, setWords] = useState<string[]>([]);
  const [ack, setAck] = useState(false);

  // Hardware Back steps through the reset sub-flow instead of leaving the screen.
  useBackHandler(phase === 'warn', () => setPhase('revealed'));
  useBackHandler(phase === 'reset', () => setPhase('warn'));

  const revealSeed = useCallback(
    async (reauthToken?: string): Promise<boolean> => {
      if (!token) {
        setWords(mockSeed); // dev/browser fallback
        setPhase('revealed');
        return true;
      }
      if (!wallet) return false;
      const m = await getWalletSeedOrThrow(token, wallet, reauthToken).then(
        (r) => r.mnemonic,
        () => null,
      );
      if (!m) return false;
      setWords(m.trim().split(/\s+/));
      setPhase('revealed');
      return true;
    },
    [token, wallet],
  );

  const doReset = useCallback(
    async (reauthToken?: string): Promise<boolean> => {
      try {
        if (token) await resetWalletApi(token, reauthToken);
      } catch {
        showToast(t('security.resetFailed'));
        return false;
      }
      await clearAllSeeds(wallets).catch(() => {});
      // Return to onboarding: empty the wallet list so the gate re-evaluates, drop
      // the local onboarded flag, and clear the route stack we're sitting on.
      qc.setQueryData(['wallets'], []);
      void qc.invalidateQueries({ queryKey: ['wallets'] });
      void qc.invalidateQueries({ queryKey: ['me'] });
      onboardingReset();
      setTab('wallet'); // land on the wallet home after onboarding, not Profile
      return true;
    },
    [token, qc, showToast, t, onboardingReset, setTab, wallets],
  );

  // ── Reauth gate (reveal) ──
  if (phase === 'reveal') {
    return (
      <ReauthGate
        onAuthed={revealSeed}
        bioTitle={t('security.retrieving')}
        bioHint={t('security.retrievingHint')}
      />
    );
  }

  // ── Reauth gate (confirm reset) ──
  if (phase === 'reset') {
    return (
      <ReauthGate
        onAuthed={doReset}
        bioTitle={t('security.resetVerifying')}
        bioHint={t('security.resetVerifyingHint')}
      />
    );
  }

  // ── Warning + responsibility acknowledgement ──
  if (phase === 'warn') {
    return (
      <div className="flex min-h-full flex-col bg-surface">
        <div className="push-enter flex flex-1 flex-col gap-6 px-4 pb-44 pt-10">
          <div className="flex flex-col items-center gap-4 text-center">
            <div className="grid size-16 place-items-center rounded-full bg-negative-soft">
              <FigIcon name="shieldSecurity" size={32} />
            </div>
            <h1 className="text-[24px] font-medium text-ink">{t('security.resetTitle')}</h1>
            <p className="text-[15px] leading-[1.44] text-muted">{t('security.resetBody')}</p>
          </div>
          <div className="rounded-[14px] bg-negative-soft p-4 text-[14px] leading-[1.5] text-negative">
            {t('security.resetWarning')}
          </div>
          <button
            onClick={() => {
              haptic();
              setAck((a) => !a);
            }}
            className="flex items-start gap-3 text-left"
          >
            <span
              className={`mt-0.5 grid size-6 shrink-0 place-items-center rounded-[7px] border text-[13px] font-bold ${
                ack ? 'border-negative bg-negative text-white' : 'border-subtle bg-card text-transparent'
              }`}
            >
              ✓
            </span>
            <span className="text-[14px] leading-[1.5] text-ink">{t('security.resetAck')}</span>
          </button>
        </div>
        <div className="fixed inset-x-0 bottom-0 flex flex-col gap-1 bg-gradient-to-t from-surface via-surface px-4 pb-[max(24px,var(--tg-safe-bottom))] pt-4">
          <button
            disabled={!ack}
            onClick={() => {
              haptic();
              setPhase('reset');
            }}
            className="flex h-14 w-full items-center justify-center rounded-full bg-negative text-[15px] font-bold text-white transition active:scale-[0.99] disabled:opacity-40 disabled:active:scale-100"
          >
            {t('security.resetConfirm')}
          </button>
          <button
            onClick={() => {
              haptic();
              setPhase('revealed');
            }}
            className="flex h-12 w-full items-center justify-center text-[15px] font-medium text-muted active:opacity-70"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    );
  }

  // ── Revealed: the word grid (12 or 24) + red reset button ──
  const half = Math.ceil(words.length / 2);
  const col1 = words.slice(0, half);
  const col2 = words.slice(half);
  const Cell = ({ n, word }: { n: number; word: string }) => (
    <div className="flex w-full items-center gap-1 rounded-[12px] bg-card p-2 text-[15px] leading-[1.44]">
      <span className="w-[22px] shrink-0 text-center text-muted">{n}.</span>
      <span className="truncate font-medium text-ink">{word}</span>
    </div>
  );
  return (
    <div className="flex min-h-full flex-col bg-surface">
      <div className="push-enter flex flex-1 flex-col items-center gap-6 px-4 pb-40 pt-8">
        <div className="flex flex-col items-center gap-4 text-center">
          <h1 className="text-[24px] font-medium text-ink">{t('security.seedTitle')}</h1>
          {wallet && <p className="text-[15px] font-medium text-ink">{wallet.name}</p>}
          <p className="w-[259px] text-[15px] leading-[1.44] text-muted">
            {t('security.seedSubtitle')}
          </p>
          {sharedWith.length > 0 && (
            <p className="max-w-[320px] rounded-[14px] bg-card p-3 text-[13px] leading-[1.44] text-muted">
              {t('security.seedSharedLegacy', { wallets: sharedWith.map((w) => w.name).join(', ') })}
            </p>
          )}
        </div>
        <div className="flex gap-6">
          <div className="flex w-[144px] flex-col gap-3">
            {col1.map((word, i) => (
              <Cell key={i} n={i + 1} word={word} />
            ))}
          </div>
          <div className="flex w-[144px] flex-col gap-3">
            {col2.map((word, i) => (
              <Cell key={i} n={i + half + 1} word={word} />
            ))}
          </div>
        </div>
      </div>
      <div className="fixed inset-x-0 bottom-0 bg-gradient-to-t from-surface via-surface px-4 pb-[max(24px,var(--tg-safe-bottom))] pt-4">
        <button
          onClick={() => {
            haptic();
            setAck(false);
            setPhase('warn');
          }}
          className="flex h-14 w-full items-center justify-center rounded-full bg-negative text-[15px] font-bold text-white shadow-[0px_0px_20px_rgba(236,50,64,0.25)] active:scale-[0.99]"
        >
          {t('security.resetButton')}
        </button>
      </div>
    </div>
  );
}
